import { Mail } from 'lucide-react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Badge, FormControl, OverlayTrigger, Popover, Table } from 'react-bootstrap';
import { getCompanyEmail } from '../../helpers/datasheetConstants';

const getValue = (admin, header) => {
  let value = header.key === 'email' ? getCompanyEmail(admin) || admin[header.key] : admin[header.key];

  if (header.valueRenderer) return header.valueRenderer(value);
  if (header.type !== 'boolean' && header.key !== 'isEnabled' && !value) return '-';
  if (header.formType === 'checkbox-group' && !value.length) return '-';

  if (header.key === 'isEnabled') {
    return value ? (
      <Badge className="p-1 px-2" style={{ fontSize: 12 }} variant={header.badgeVariant || 'success'}>
        Enabled
      </Badge>
    ) : (
      <Badge className="p-1 px-2" style={{ fontSize: 12 }} variant={header.badgeVariant || 'danger'}>
        Disabled
      </Badge>
    );
  }

  if (Array.isArray(value)) value = value.join(', ');
  let returnElement = typeof value === 'object' ? value[header.objectKey] : value;

  switch (header.type) {
    case 'email':
      returnElement = (
        <a onClick={e => e.stopPropagation()} href={`mailto:${value}`} className="text-dark">
          {value}
        </a>
      );
      break;
    case 'tags-input':
      const emailsArray = value.split(',').map(e => e.trim());
      const visibleEmails = emailsArray.slice(0, 2);
      const extraEmails = emailsArray.slice(2);
      return (
        <div onClick={e => e.stopPropagation()}>
          {visibleEmails.map((email, i) => (
            <div key={i}>{email}</div>
          ))}
          {extraEmails.length > 0 && (
            <OverlayTrigger
              trigger="click"
              rootClose
              placement="bottom"
              overlay={
                <Popover id="popover-emails" className="p-2">
                  <p className="mb-0 font-weight-semibold midFont">Additional Emails</p>
                  {extraEmails.map((email, i) => (
                    <div key={i} className="d-flex align-items-center midFont">
                      <Mail size={12} className="mr-1" />
                      {email}
                    </div>
                  ))}
                </Popover>
              }
            >
              <span className="cursor-pointer underline">{`+${extraEmails.length} more`}</span>
            </OverlayTrigger>
          )}
        </div>
      );
      break;
    case 'phone':
      returnElement = (
        <a onClick={e => e.stopPropagation()} href={`tel:${value}`} className="text-dark">
          {value}
        </a>
      );
      break;
    case 'date':
      returnElement = moment(value).format('MMM DD YYYY');
      break;
    case 'boolean':
    case 'checkbox':
      returnElement = returnElement ? 'Yes' : 'No';
      break;
    case 'number':
      returnElement = returnElement.toFixed(header.fixedDigits || 2);
      break;
  }

  if (header.showAsBadge) {
    returnElement = (
      <Badge className="p-1 px-2" style={{ fontSize: 12 }} variant={header.badgeVariant || 'warning'}>
        {returnElement}
      </Badge>
    );
  }

  if (header.linkToKey) {
    let url = admin[header.linkToKey] || '';
    if (!url.startsWith('https://') && !url.startsWith('http://')) url = 'https://' + url;
    returnElement = (
      <a target="_blank" href={url}>
        {returnElement}
      </a>
    );
  }

  return returnElement;
};

const getKey = (header, idx) => {
  if (header.name && typeof header.name === 'string') return header.name;
  if (header.key && typeof header.key === 'string') return header.key;
  return `header-${idx}`;
};

const EntityTable = ({ headers, data = [], rowKeyField, searchQueryKeys = [], actionCell, additionalComponents }) => {
  const [filteredData, setFilteredData] = useState(data);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setQuery('');
    setFilteredData(data);
  }, [data]);

  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) return setFilteredData(data);

    setFilteredData(
      data.filter(d =>
        searchQueryKeys.some(
          k =>
            d[k] &&
            d[k]
              .toString()
              .toLowerCase()
              .includes(q)
        )
      )
    );
  }, [query, data, searchQueryKeys]);

  return (
    <div className="d-flex flex-column h-100" style={{ minHeight: 0, overflow: 'hidden' }}>
      <div className="d-flex mb-4 w-100" style={{ flexShrink: 0 }}>
        <div className="flex-grow-1">
          <FormControl
            size="sm"
            placeholder="Quick search..."
            className="entity-table-input-field"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        {additionalComponents && <div className="ml-2">{additionalComponents()}</div>}
      </div>
      {/* Responsive Table */}
      <div className="table-responsive border p-0" style={{ borderRadius: '10px' }}>
        <Table hover className="m-0">
          <thead style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#fff' }}>
            <tr style={{ borderBottom: '1px solid #dee2e6' }}>
              {headers.map((header, index) => (
                <th
                  key={getKey(header, index)}
                  style={{
                    minWidth: header.minWidth || 100,
                    padding: '10px',
                    textAlign: 'left',
                    color: '#6c757d'
                  }}
                >
                  {header.name}
                </th>
              ))}
              {actionCell && <th style={{ minWidth: 70, padding: '10px', color: '#6c757d' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map(dataObj => (
                <tr key={dataObj[rowKeyField]} style={{ borderBottom: '1px solid #f1f3f4' }}>
                  {headers.map((header, index) => (
                    <td
                      key={getKey(header, index)}
                      style={{
                        padding: '12px 16px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        verticalAlign: 'middle'
                      }}
                    >
                      {getValue(dataObj, header)}
                    </td>
                  ))}
                  {actionCell && <td style={{ padding: '12px 16px' }}>{actionCell(dataObj)}</td>}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={actionCell ? headers.length + 1 : headers.length} style={{ padding: '12px 16px' }}>
                  <h6 className="text-muted text-center mb-0">Nothing to show</h6>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default EntityTable;
